import { aDesign, Design, DesignState } from 'base/styleguide/model';
import { action, computed, makeObservable } from 'mobx';
import { ProjectSession } from '../session/session';
import {
  ConceptAPI,
  getGlobalConceptsStore,
} from '../styleguide/concept_store';

class DesignStore {
  constructor(private readonly session: ProjectSession) {
    makeObservable(this);
  }

  @computed
  get design(): DesignAPI {
    const design = this.session.project.design.current;
    if (design == null) {
      throw new Error('Design not initialized');
    }

    return new DesignAPI(design);
  }

  @action
  ensureDesignInitialized() {
    if (this.session.project.design.current != null) return;

    const conceptsStore = getGlobalConceptsStore();

    this.session.undo.doPermanently(() => {
      // Try initializing the design with the first concept.
      let concept = conceptsStore.concepts[0];
      if (concept == null) {
        // Style guide tab probably hasn't been opened yet... create a concept so
        // we have something to link to.
        concept = conceptsStore.createNewConcept({
          createdBy: this.session.createdBy,
        });
      }
      this.setLinkedConcept(concept);
    });
  }

  @action
  setLinkedConcept(concept: ConceptAPI) {
    this.session.project.design.replace(
      aDesign({ conceptId: concept.state.uuid }),
    );
  }
}

let globalDesignStore: DesignStore | null = null;

export const initGlobalDesignStore = (session: ProjectSession) => {
  globalDesignStore = new DesignStore(session);
};

export const getGlobalDesignStore = (): DesignStore => {
  if (globalDesignStore == null) {
    throw new Error('Design store not initialized');
  }
  return globalDesignStore;
};

class DesignAPI {
  constructor(private readonly atom: Design) {
    makeObservable(this);
  }

  @computed
  get state(): DesignState {
    return this.atom.state;
  }

  @computed
  get linkedConcept(): ConceptAPI {
    const conceptsStore = getGlobalConceptsStore();
    let concept = conceptsStore.concepts.find(
      (c) => c.state.uuid === this.state.conceptId,
    );
    if (concept == null) {
      // Default to the first concept
      concept = conceptsStore.concepts[0];
      if (concept == null) {
        throw new Error('No concepts found');
      }
    }
    return concept;
  }
}
export type { DesignAPI };
